import { useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { useMediaQuery } from 'usehooks-ts';

import QuantaIcon from '@/components/QuantaIcon';
import { withErrorSupportAction } from '@/components/support/error-support-action';
import { useIntlLocale } from '@/i18n/intl-locale';
import { useNetwork } from '@/stores/useAppStore';
import { markAsViewed } from '@/stores/useNotificationStore';
import type { NotificationItem } from '@/types/notification.types';
import { cn } from '@/utils/cn';
import { formatDateToMonthNumber } from '@/utils/formatDateToMonthNumber';

import { Button } from '../button';
import TelegramSetupInstructions from '../TelegramSetupInstructions';

import Icons from './Icons';
import ToastHeaderRightSection from './ToastHeaderRightSection';
import ToastProgress from './ToastProgress';
import { handleMouseEnter, handleMouseLeave, hydrateActionButtons, hydrateActionLinks, resolveNotificationIconKey } from './toast.helpers';

interface ToastProps extends Omit<NotificationItem, 'id'> {
  id: string | number; // when using built-in toastId its a number. Notification id is always a string.
  isNotificationCenterItem?: boolean; // when true, shows as notification center item (with date, no close button)
  isNotification?: boolean; // when true, notification is stored in localStorage and should be marked as viewed on close
  showProgressBar?: boolean; // default: false
  duration?: number; // duration in milliseconds
}

function Toast({
  id,
  type,
  title,
  description,
  actionButtons: actionButtonsProp,
  actionLinks: actionLinksProp,
  metadata,
  isViewed,
  createdAt,
  isNotificationCenterItem = false,
  isNotification = false,
  showProgressBar = true,
  duration = 5000,
}: ToastProps) {
  const t = useTranslations('common.toast');
  const network = useNetwork();
  const isMobile = useMediaQuery('(max-width: 767px)'); // md breakpoint

  const [isHovered, setIsHovered] = useState<boolean>(false);

  // Hydrate serializable actions to render-ready elements
  const actionButtons = useMemo(
    () =>
      hydrateActionButtons(
        { details: t('details'), enableTelegram: t('enableTelegram') },
        withErrorSupportAction(type, actionButtonsProp),
        metadata,
        id,
        network,
        actionLinksProp,
      ),
    [t, type, id, actionButtonsProp, actionLinksProp, metadata, network],
  );
  const actionLinks = useMemo(() => hydrateActionLinks(actionLinksProp), [actionLinksProp]);
  const intlLocale = useIntlLocale();
  const formattedDate = useMemo(
    () => (isNotificationCenterItem ? formatDateToMonthNumber(createdAt, intlLocale) : null),
    [isNotificationCenterItem, createdAt, intlLocale],
  );

  const shouldBeInteractive = isMobile && isNotificationCenterItem;

  const isProgress = type === 'progress';
  const isSwapDrawerProgress = isProgress && Boolean(metadata?.swapFlow);
  const isQuanta = type === 'quanta';
  const IconComponent = Icons[resolveNotificationIconKey(type, metadata, actionLinksProp)];

  const handleClose = (): void => {
    // Mark as viewed when closing persistent notifications (stored in localStorage)
    if (isNotification) {
      markAsViewed(id);
    }

    toast.dismiss(id);
  };

  const handleItemMouseEnter = (): void => {
    handleMouseEnter(id);

    if (isNotificationCenterItem && !isMobile) {
      setIsHovered(true);
    }
  };

  const handleItemMouseLeave = (): void => {
    handleMouseLeave(id);

    if (isNotificationCenterItem && !isMobile) {
      setIsHovered(false);
    }
  };

  const handleItemClick = (): void => {
    if (isMobile && isNotificationCenterItem) {
      setIsHovered((prev) => !prev);
    }
  };

  const handleItemKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ') && isMobile && isNotificationCenterItem) {
      e.preventDefault();
      setIsHovered((prev) => !prev);
    }
  };

  return (
    <div
      id={String(id)}
      role="alert"
      aria-live="polite"
      className={cn(
        'relative w-full sm:w-115 p-3 bg-surface-secondary rounded-lg shadow-[0_0_26.4px_2px_var(--color-toast-shadow)] ring-1 ring-inset ring-toast-border overflow-hidden',
        formattedDate && 'sm:w-full rounded-none border-b border-border-primary shadow-none',
        isNotificationCenterItem && 'group/notification',
        isViewed && isNotificationCenterItem && 'bg-surface-primary',
        showProgressBar && !isNotificationCenterItem && 'pb-4', // p-3 (padding is used for the content) + h-1 (progress bar height)
        isNotificationCenterItem && metadata?.swapFlow && 'pb-6',
        shouldBeInteractive && 'cursor-pointer',
      )}
      onMouseEnter={handleItemMouseEnter}
      onMouseLeave={handleItemMouseLeave}
      // Mobile tap interaction with keyboard support for accessibility
      {...(shouldBeInteractive && {
        onClick: handleItemClick,
        onKeyDown: handleItemKeyDown,
        tabIndex: 0,
      })}
    >
      <div className="grid grid-cols-[auto_1fr] gap-2">
        <div className={cn('flex items-center justify-center shrink-0 h-5.75', isProgress && !isSwapDrawerProgress && 'animate-spin')}>
          {isQuanta ? <QuantaIcon alt="Quanta" /> : <IconComponent />}
        </div>
        <div className={cn('flex flex-col gap-2', isNotificationCenterItem && 'min-w-0 [overflow-wrap:anywhere]')}>
          <div className={cn('flex items-start justify-between gap-6', isNotificationCenterItem && 'items-center')}>
            <p className="font-medium text-text-primary">{title.replaceAll(' -> ', ' → ')}</p>
            <ToastHeaderRightSection
              id={id}
              isHovered={isHovered}
              isMobile={isMobile}
              isViewed={isViewed}
              formattedDate={formattedDate}
              onClose={handleClose}
            />
          </div>
          {(!!description || !!metadata?.telegramManualCommand || actionButtons.length > 0 || actionLinks.length > 0) && (
            <div className="flex flex-col gap-3 z-10">
              {description && <p className="text-sm font-medium text-text-secondary">{description}</p>}
              <TelegramSetupInstructions command={metadata?.telegramManualCommand} />
              {actionLinks.length > 0 && (
                <div className="flex flex-col gap-2 z-10">
                  {actionLinks.map(({ label, href, target, icon, color, secondaryText }) => (
                    <div key={href} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
                      <a
                        href={href}
                        {...(target && { target, rel: 'noopener noreferrer' })}
                        className={cn('inline-flex items-center gap-2 hover:underline', color || 'text-text-primary')}
                      >
                        {icon && <span className="shrink-0">{icon}</span>}
                        {label}
                      </a>
                      {secondaryText && <span className="text-text-secondary">{secondaryText}</span>}
                    </div>
                  ))}
                </div>
              )}
              {actionButtons.length > 0 && (
                <div className="flex flex-wrap items-center gap-4">
                  {actionButtons.map(({ label, variant, trailingIcon, leadingIcon, href, dismissOnClick, onClick }) => (
                    <Button
                      key={`${href} + ${label} + ${variant}`}
                      size="small"
                      variant={variant}
                      trailingIcon={trailingIcon}
                      leadingIcon={leadingIcon}
                      href={href}
                      onClick={() => {
                        onClick?.();

                        if (dismissOnClick) {
                          handleClose();
                        }
                      }}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <ToastProgress
        shouldShowProgress={showProgressBar && !isNotificationCenterItem}
        createdAt={createdAt}
        toastId={id}
        duration={duration}
      />
      {isNotificationCenterItem && metadata?.swapFlow && typeof id === 'string' && (
        <span className="absolute bottom-1.5 right-3 text-xs text-gray-400 pointer-events-none">{id.slice(0, 12)}</span>
      )}
    </div>
  );
}

export default Toast;
